// components/seo/PageMeta.jsx
// Canonical per-page SEO component for Trinity Dental Arts.
// Handles: <title>, meta description, canonical URL, robots meta, Open Graph,
// Twitter Card, and JSON-LD (rendered as real <script> via portal so Prerender
// captures it in the initial HTML snapshot crawlers receive).
//
// Titles/descriptions are DETERMINISTIC — never rewritten client-side. The
// site-wide @graph (WebSite + Dentist + Person) lives in index.html as a static
// block visible to every crawler, including non-JS agents.

import { useEffect } from 'react';
import { pageContent } from '@/lib/seo/pageContent';
import { getPageSocialImage } from '@/lib/seo/socialImages';
import { createPortal } from 'react-dom';

const SITE_ORIGIN = 'https://trinitydentalarts.com';
const DEFAULT_OG_IMAGE = 'https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/697e88817b05b4051a928e3f/97657042f_33363942_975460192617351_6697685401403916288_o.jpg';

const normalizePath = (path) => {
  if (!path || path === '/') return '/';
  return path.replace(/\/+$/, '');
};

function JsonLdScript({ id, data }) {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <script
      type="application/ld+json"
      data-page-schema={id}
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(data, null, 0)
          .replace(/</g, '\\u003c')
          .replace(/>/g, '\\u003e')
          .replace(/&/g, '\\u0026'),
      }}
    />,
    document.head
  );
}

export default function PageMeta({
  title,
  description,
  path,
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt,
  ogImageWidth = 1200,
  ogImageHeight = 630,
  robots = 'index, follow, max-image-preview:large',
  jsonLd = null,
  ready = true,
}) {
  const maintained = pageContent[normalizePath(path)];
  title = maintained?.title || title;
  description = maintained?.description || description;
  const canonical = `${SITE_ORIGIN}${normalizePath(path)}`;
  const socialImage = getPageSocialImage(path);
  ogImage = socialImage?.url || ogImage;
  const imageAlt = socialImage?.alt || ogImageAlt || title;
  const imageWidth = socialImage?.width || ogImageWidth;
  const imageHeight = socialImage?.height || ogImageHeight;

  useEffect(() => {
    if (typeof window !== 'undefined') window.prerenderReady = false;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    document.title = title;

    const upsertMeta = (selector, attr, attrVal, content) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    upsertMeta('meta[name="description"]', 'name', 'description', description);
    upsertMeta('meta[name="robots"]', 'name', 'robots', robots);

    let canonicalEl = document.head.querySelector('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canonical);

    upsertMeta('meta[property="og:title"]', 'property', 'og:title', title);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', description);
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', canonical);
    upsertMeta('meta[property="og:image"]', 'property', 'og:image', ogImage);
    upsertMeta('meta[property="og:image:secure_url"]', 'property', 'og:image:secure_url', ogImage);
    upsertMeta('meta[property="og:image:alt"]', 'property', 'og:image:alt', imageAlt);
    upsertMeta('meta[property="og:image:width"]', 'property', 'og:image:width', String(imageWidth));
    upsertMeta('meta[property="og:image:height"]', 'property', 'og:image:height', String(imageHeight));
    upsertMeta('meta[property="og:type"]', 'property', 'og:type', 'website');
    upsertMeta('meta[property="og:site_name"]', 'property', 'og:site_name', 'Trinity Dental Arts');
    upsertMeta('meta[property="og:locale"]', 'property', 'og:locale', 'en_US');

    upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', ogImage);
    upsertMeta('meta[name="twitter:image:alt"]', 'name', 'twitter:image:alt', imageAlt);
    upsertMeta('meta[name="twitter:url"]', 'name', 'twitter:url', canonical);

    if (!ready) return;
    const readyTimer = setTimeout(() => {
      if (typeof window !== 'undefined') window.prerenderReady = true;
    }, 250);
    return () => clearTimeout(readyTimer);
  }, [title, description, canonical, ogImage, imageAlt, imageWidth, imageHeight, robots, ready]);

  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  const hasBreadcrumb = schemas.some((s) => s && s['@type'] === 'BreadcrumbList');

  // A page-supplied BreadcrumbList is authoritative: drop the path-derived one the
  // global BreadcrumbSchema component may already have injected (lazy pages mount
  // after that component's route-change effect has run).
  useEffect(() => {
    if (typeof document === 'undefined' || !hasBreadcrumb) return;
    document.querySelectorAll('script[data-breadcrumb-schema]').forEach((s) => s.remove());
  }, [hasBreadcrumb, path]);

  return (
    <>
      {schemas.map((schema, i) => (
        <JsonLdScript key={`${path}-schema-${i}`} id={`${path}-schema-${i}`} data={schema} />
      ))}
    </>
  );
}
